/**
 * SUB-5.10 · FR44. Dashboards that exist without a row.
 *
 * The All Accounts overview appears in the Dashboards surface, is where the
 * Dashboards row lands in merged scope, and is selectable from the picker. It is
 * not a `dashboards` record and must never become one.
 *
 * ⚠️ **D51 STANDS, AND ITS THREE PREMISES WERE RE-VERIFIED AGAINST THE LIVE
 * CATALOG ON 2026-08-17 RATHER THAN TRUSTED FROM A COMMENT.**
 *
 *   1. `clone_tenant_config` exists and references `dashboards`, and
 *      `dashboards.cloned_from_dashboard_id` exists. Both confirmed from
 *      `pg_proc` and `pg_attribute`. **A portfolio that is a row is a row that
 *      can be cloned INTO a client account**, putting a cross-account surface
 *      inside an account that must not know a hierarchy exists (FR19, ISO1).
 *      A route cannot be cloned.
 *   2. `dashboards.tenant_id` is NOT NULL, confirmed. A row would need the
 *      anchor-plus-mode dance D24 invented for sessions, for no gain.
 *   3. FR45 stays true by construction rather than by care. This module reads no
 *      dashboard table, writes none, and adds no column to any.
 *
 * ⚠️ **THIS WILL COME UNDER PRESSURE, AND THE PRESSURE WILL SOUND REASONABLE.**
 * Once it looks like a dashboard, someone will ask to add a widget to it, then
 * to save a layout, and a `dashboards` row is the obvious way to store either.
 * It is also the way a cross-account surface ends up inside a client account.
 * `PortfolioRollup` already says of itself: "it is a fixed composition, not a
 * widget canvas." If per-user layout is genuinely wanted, store it against the
 * USER, never against a dashboards row.
 *
 * ⚠️ **THE ID MUST NOT BE UUID-SHAPED.** It shares a URL segment with real
 * `dashboards.id` values, and a uuid-shaped built-in could one day collide with
 * a real row. A slug cannot. `builtinDashboards.test.ts` asserts this.
 */

import { isMergedScope, offersMergedScope, type MergedScopeStatus } from './mergedScope';

/**
 * The route segment and picker value for the All Accounts overview.
 *
 * ⚠️ Deliberately not a uuid. See the header.
 */
export const ALL_ACCOUNTS_DASHBOARD_ID = 'all-accounts';

/**
 * What this screen is called, everywhere it is named.
 *
 * ⚠️ NOT "Portfolio". The mode is called All Accounts in the account switcher
 * and in every scope notice, and a screen named for the mode it belongs to needs
 * no explaining. "Portfolio" is a good page name and a vague dashboard name.
 *
 * ⚠️ **D91, 2026-08-18: ONE NAME, AND THE WORD "overview" IS GONE.** This screen
 * had THREE names — the nav row said Dashboards, the picker said "All Accounts
 * overview", and the page heading said "Portfolio". The heading was a leftover:
 * SUB-5.11 deleted the Portfolio nav row, so that word survived in exactly one
 * place in the product and named something the user had never otherwise seen.
 *
 * "overview" went with it. It was doing work when this entry sat in a list
 * beside an account's own dashboards and had to be distinguished from them. D89
 * made it the ONLY entry in merged scope, so there is nothing left to
 * distinguish it from and the noun was describing the list rather than the
 * screen.
 *
 * ⚠️ **THE NAV ROW STAYS "Dashboards", AND THAT IS NOT AN OVERSIGHT.** SUB-5.10's
 * whole premise is that people go looking for this under the word Dashboards. A
 * nav row that renames itself when the scope changes would move the screen out
 * from under that word in the one mode that needs it most.
 */
export const ALL_ACCOUNTS_DASHBOARD_NAME = 'All Accounts';

export interface BuiltinDashboard {
  id: string;
  name: string;
}

/** Whether an id from the URL or the picker names a built-in rather than a row. */
export function isBuiltinDashboardId(id: string | null | undefined): boolean {
  return id === ALL_ACCOUNTS_DASHBOARD_ID;
}

/**
 * The built-ins to list in the picker for this scope.
 *
 * ⚠️ Gated on `offersMergedScope`, the same predicate the Portfolio nav row and
 * the page itself already use, so the entry appears exactly when the page will
 * render rather than redirect. Someone with no sub-accounts never sees it and
 * learns nothing about whether hierarchies exist (FR19).
 */
export function builtinDashboardsFor(status: MergedScopeStatus): BuiltinDashboard[] {
  if (!offersMergedScope(status)) return [];
  return [{ id: ALL_ACCOUNTS_DASHBOARD_ID, name: ALL_ACCOUNTS_DASHBOARD_NAME }];
}

/**
 * Where the Dashboards row should land when nothing is remembered for this
 * scope, or `null` to use the existing "default dashboard, else first" path.
 *
 * ⚠️ **D89 MADE IT A DESTINATION IN MERGED SCOPE, AND THIS COMMENT USED TO SAY
 * THE OPPOSITE.** It read: "a remembered dashboard for this scope wins, because
 * SUB-5.9 made that memory scope-aware precisely so an explicit later choice
 * survives." That was right while merged scope still offered other dashboards to
 * choose. It does not: D89 leaves exactly one entry in the picker, so there is no
 * explicit later choice to preserve, and honouring a remembered id could only
 * ever send the user to a dashboard that is now redirected anyway.
 *
 * ⚠️ The memory itself is untouched and still scope-keyed, so switching back to a
 * single account still lands where you left it there.
 *
 * ⚠️ Gated on `isMergedScope`, not `offersMergedScope`: someone who COULD enter
 * merged scope but has not should land on their own account's dashboards, which
 * are the ones that describe where they actually are.
 */
export function defaultDashboardIdForScope(status: MergedScopeStatus): string | null {
  return isMergedScope(status) ? ALL_ACCOUNTS_DASHBOARD_ID : null;
}

/**
 * Whether the Dashboards surface should offer this account's OWN dashboards.
 *
 * ⚠️ **FALSE IN MERGED SCOPE. THIS QUESTION HAS BEEN ANSWERED FOUR TIMES AND
 * D89 IS THE ONE THAT COUNTS. READ ALL FOUR BEFORE TOUCHING IT.**
 *
 * | | | |
 * |---|---|---|
 * | SUB-5.11, first | Label | Mine, built from an unanswered question |
 * | SUB-5.11, same day | Hide | Reversed hours later; this module deleted |
 * | D88 | Label | Allan's, restored the module |
 * | **D89** | **Hide, and REDIRECT** | **Allan's. The overview IS the dashboard in All Accounts** |
 *
 * ⚠️ **D88 AND D89 ARE NOT THE SAME QUESTION ASKED TWICE.** D88 chose between a
 * label and the hide AS BUILT, and the hide as built was broken: it filtered
 * this list and nothing else, so the route still rendered a tenant dashboard,
 * the picker held a value with no matching item, and the header went blank.
 * Allan reported that as "a dashboard without a name". D89 chose the hide once
 * it also redirects. **A hide that only hides the label is strictly worse than a
 * label, and that is what D88 was actually rejecting.**
 *
 * So this flag is now HALF the answer, and shipping it alone is the defect:
 *
 *   1. this list drops them, and Create with them;
 *   2. `DashboardView` REDIRECTS a tenant dashboard id to the overview;
 *   3. `Sidebar` sends the Dashboards row to the overview BEFORE consulting the
 *      remembered dashboard, which `defaultDashboardIdForScope` now says.
 *
 * ⚠️ Nothing is lost. Those dashboards are one account switch away and
 * unchanged, and a dashboard covering one account is a different question from
 * one covering every account, not a smaller view of it. `getDashboards` pins to
 * the anchor, so it is always the SAME one account.
 *
 * ⚠️ CREATING ONE IS HIDDEN FOR THE SAME REASON. A dashboard created here would
 * be written against the anchor and then immediately be invisible, which is a
 * worse state than not offering it.
 */
export function tenantDashboardsVisibleIn(status: MergedScopeStatus): boolean {
  return !isMergedScope(status);
}

/**
 * D89 · Should this dashboard id be redirected to the overview?
 *
 * ⚠️ **THE HALF SUB-5.11'S HIDE WAS MISSING.** Hiding an entry from a picker
 * does not make the route unreachable: the sidebar remembers ids (SUB-5.9), the
 * browser remembers URLs, and both point straight past the picker. Without this,
 * All Accounts renders a one-account dashboard with a blank name.
 */
export function shouldRedirectToOverview(
  status: MergedScopeStatus,
  dashboardId: string | null | undefined,
): boolean {
  if (!isMergedScope(status)) return false;
  if (!dashboardId) return false;
  return !isBuiltinDashboardId(dashboardId);
}
